import { getBrandConfig } from '@axo/config';
import { getProductSiteMap } from '@axo/config/site';
import { getSiteOneHost } from '@axo/config/site/multi-tenant/get-site-one-host';
import type { Brand } from '@axo/config/@types/Brand.types';
import { loan_application } from '@axo/shared/data-access/types';

type MyPageProductKey =
  | 'my-page-loans'
  | 'my-page-credit-cards'
  | 'my-page-secured-loans';

export type MyPageEnv = Parameters<typeof getSiteOneHost>[1];

const productKeyFor = (
  product: loan_application.Product | undefined
): MyPageProductKey => {
  switch (product) {
    case loan_application.Product.CreditCard:
      return 'my-page-credit-cards';
    case loan_application.Product.SecuredLoan:
      return 'my-page-secured-loans';
    default:
      return 'my-page-loans';
  }
};

/** site-one's own route for the product — used where no branded proxy exists */
const siteOnePathFor = (
  product: loan_application.Product | undefined
): string => {
  switch (product) {
    case loan_application.Product.CreditCard:
      return '/mypage/credit-cards';
    case loan_application.Product.SecuredLoan:
      return '/mypage/secured-loans';
    default:
      return '/mypage/loans';
  }
};

/**
 * Builds the PUBLIC, customer-safe MyPage URL for an application.
 *
 * Production: real brand domain + localized, product-correct path. Resolution
 * (first hit wins): product-detail path → overview → legacy `mypage`.
 * `getProductSiteMap` already collapses detail → overview; `?? site.path.mypage`
 * adds the legacy rung.
 *
 * Staging / local: the localized paths only exist behind the branded proxy
 * (site-axo), which has no staging deployment — so target site-one's host and
 * its own `/mypage/*` routes instead.
 *
 * `cid` carries the ACE agent id on both links so the visit can be attributed;
 * `traffic: 'internal'` is only set for the in-ACE "Go to MyPage" link, never
 * for the copied customer link.
 *
 * Returns `null` when the brand or token is missing, or the brand is unknown to
 * the registry.
 */
export const buildPublicMyPageUrl = ({
  brand,
  product,
  magicToken,
  env,
  traffic,
  clientId,
}: {
  brand: Brand | undefined;
  product: loan_application.Product | undefined;
  magicToken: string | undefined | null;
  env: MyPageEnv;
  traffic?: 'internal';
  clientId?: string | null;
}): string | null => {
  if (!brand || !magicToken) return null;

  try {
    const { site } = getBrandConfig(brand, ['site']);
    if (!site) return null;

    const url =
      env === 'production'
        ? brandedUrl(site, product)
        : new URL(siteOnePathFor(product), getSiteOneHost(site.domain, env));
    if (!url) return null;

    url.searchParams.set('id', magicToken);
    if (traffic) url.searchParams.set('traffic', traffic);
    if (clientId) url.searchParams.set('cid', clientId);
    return url.toString();
  } catch {
    return null;
  }
};

const brandedUrl = (
  site: NonNullable<ReturnType<typeof getBrandConfig>['site']>,
  product: loan_application.Product | undefined
): URL | null => {
  const pathOrUrl =
    getProductSiteMap(site.path)[productKeyFor(product)] ?? site.path.mypage;
  if (!pathOrUrl) return null;

  // WHATWG URL resolves relative-vs-absolute paths and merges the query:
  //   ('/mine-tilbud/forbrukslan', 'https://axofinans.no') → https://axofinans.no/mine-tilbud/forbrukslan
  //   ('https://mypage.axolaina.fi', 'https://uscore.fi')  → https://mypage.axolaina.fi/  (absolute wins)
  return new URL(pathOrUrl, `https://${site.domain}`);
};
